import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import toast from 'react-hot-toast';
import { Announcement, PulseSeverity } from '../types';

export interface ReadReceipt {
  userId: string;
  userName: string;
  readAt: string;
  acknowledgedAt?: string;
}

const SEVERITY_WEIGHTS: Record<PulseSeverity, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

const sortAnnouncements = (a: Announcement, b: Announcement) => {
  const weightA = SEVERITY_WEIGHTS[a.severity];
  const weightB = SEVERITY_WEIGHTS[b.severity];

  if (weightA !== weightB) {
    return weightA - weightB;
  }

  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
};

/**
 * Fetches announcements for a specific node, sorted by severity then date.
 */
export const useAnnouncements = (nodeId: string, recursive: boolean = true) => {
  return useQuery<Announcement[]>({
    queryKey: ['announcements', nodeId, recursive],
    queryFn: async () => {
      if (!nodeId) return [];
      const targetId = nodeId === 'platform-root' ? 'global' : nodeId;
      const response = await apiWorker.get(`/api/nodes/${targetId}/pulse`, {
        params: { recursive }
      });
      const data = Array.isArray(response.data?.content)
        ? response.data.content 
        : (Array.isArray(response.data) ? response.data : []);
      return [...data].sort(sortAnnouncements);
    },
    enabled: !!nodeId,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (
        error?.response?.status === 401 || 
        error?.response?.status === 403 || 
        error?.response?.status === 429 ||
        error?.response?.status === 404
      ) return false;
      return failureCount < 1;
    }
  });
};

/**
 * Fetches announcements across all organizaciones the user is a member of.
 */
export const useUniversalAnnouncements = (enabled: boolean = true) => {
  return useQuery<Announcement[]>({
    queryKey: ['announcements', 'universal'],
    queryFn: async () => {
      const response = await apiWorker.get(`/api/users/me/announcements`);
      const data = Array.isArray(response.data?.content)
        ? response.data.content 
        : (Array.isArray(response.data) ? response.data : []);
      return [...data].sort(sortAnnouncements);
    },
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (
        error?.response?.status === 401 || 
        error?.response?.status === 403 || 
        error?.response?.status === 429 ||
        error?.response?.status === 404
      ) return false;
      return failureCount < 1;
    }
  });
};

/**
 * Mutation to create a new announcement with optimistic updates.
 */
export const useCreateAnnouncement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newAnnouncement: Partial<Announcement>) =>
      apiWorker.post(`/api/nodes/${nodeId}/pulse`, newAnnouncement).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onMutate: async (newAnnouncement) => {
      await queryClient.cancelQueries({ queryKey: ['announcements', nodeId] });
      const previousAnnouncements = queryClient.getQueryData<Announcement[]>(['announcements', nodeId]);

      if (previousAnnouncements) {
        const optimisticAnnouncement = {
          ...newAnnouncement,
          id: 'temp-' + Date.now(),
          nodeId,
          createdAt: new Date().toISOString(),
          relayCount: 0,
          totalRecipients: 0,
          authorUserId: 'me', // Temporary ID for optimistic UI
        } as Announcement;

        queryClient.setQueryData<Announcement[]>(
          ['announcements', nodeId],
          [optimisticAnnouncement, ...previousAnnouncements].sort(sortAnnouncements)
        );
      }

      return { previousAnnouncements };
    },
    onError: (err, newAnnouncement, context) => {
      if (context?.previousAnnouncements) {
        queryClient.setQueryData(['announcements', nodeId], context.previousAnnouncements);
      }
      toast.error('Failed to publish announcement');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements', nodeId] });
    },
    onSuccess: () => {
      toast.success('Announcement published');
    },
  });
};

/**
 * Mutation to acknowledge an announcement.
 */
export const useAcknowledgeAnnouncement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) =>
      apiWorker.post(`/api/nodes/${nodeId}/pulse/${announcementId}/acknowledge`),
    onMutate: async (announcementId) => {
      await queryClient.cancelQueries({ queryKey: ['announcements', nodeId] });
      const previousAnnouncements = queryClient.getQueryData<Announcement[]>(['announcements', nodeId]);
      
      if (previousAnnouncements) {
        queryClient.setQueryData<Announcement[]>(['announcements', nodeId], 
          previousAnnouncements.map(a => 
            a.id === announcementId 
              ? { ...a, isAcknowledged: true, acknowledgedBy: [...(a.acknowledgedBy || []), 'current-user'] } 
              : a
          )
        );
      }
      
      return { previousAnnouncements };
    },
    onError: (err, announcementId, context) => {
      if (context?.previousAnnouncements) {
        queryClient.setQueryData(['announcements', nodeId], context.previousAnnouncements);
      }
      toast.error('Failed to acknowledge announcement');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements', nodeId] });
    },
  });
};

/**
 * Mutation to relay an announcement downstream.
 */
export const useRelayAnnouncement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) =>
      apiWorker.post(`/api/nodes/${nodeId}/pulse/${announcementId}/relay`),
    onMutate: async (announcementId) => {
      await queryClient.cancelQueries({ queryKey: ['announcements', nodeId] });
      const previousAnnouncements = queryClient.getQueryData<Announcement[]>(['announcements', nodeId]);
      
      if (previousAnnouncements) {
        queryClient.setQueryData<Announcement[]>(['announcements', nodeId], 
          previousAnnouncements.map(a => 
            a.id === announcementId 
              ? { ...a, relayCount: (a.relayCount || 0) + 1 } 
              : a
          )
        );
      }
      
      return { previousAnnouncements };
    },
    onSuccess: () => {
      toast.success('Announcement relayed downstream');
    },
    onError: (err, announcementId, context) => {
      if (context?.previousAnnouncements) {
        queryClient.setQueryData(['announcements', nodeId], context.previousAnnouncements);
      }
      toast.error('Failed to relay announcement');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements', nodeId] });
    },
  });
};

/**
 * Mutation to mark an announcement as read/viewed.
 */
export const useMarkAnnouncementRead = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) =>
      apiWorker.post(`/api/nodes/${nodeId}/pulse/${announcementId}/view`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements', nodeId] });
    },
  });
};

/**
 * Mutation to resolve an announcement (Admin only).
 */
export const useResolveAnnouncement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) =>
      apiWorker.post(`/api/nodes/${nodeId}/pulse/${announcementId}/resolve`),
    onSuccess: () => {
      toast.success('Announcement marked as resolved');
      queryClient.invalidateQueries({ queryKey: ['announcements', nodeId] });
    },
    onError: () => {
      toast.error('Failed to resolve announcement');
    }
  });
};

/**
 * Mutation to hard delete an announcement (Admin only).
 */
export const useDeleteAnnouncement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (announcementId: string) =>
      apiWorker.delete(`/api/nodes/${nodeId}/pulse/${announcementId}`),
    onSuccess: () => {
      toast.success('Announcement deleted');
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    onError: () => {
      toast.error('Failed to delete announcement');
    }
  });
};
